import OpenAI from 'openai';
import { getDefaultModel } from '../../default-model';
import type { Model, ModelMetadata, ModelProvider } from '../../model';
import { getDefaultOpenAIClient, getDefaultOpenAIKey } from './defaults';
import { OpenAIResponsesModel } from './responses-model';

export type OpenAIModelMetadataResolver =
  | Record<string, ModelMetadata>
  | ((modelName: string) => ModelMetadata | undefined);

export type OpenAIProviderOptions = {
  apiKey?: string;
  baseURL?: string;
  organization?: string;
  project?: string;
  openAIClient?: OpenAI;
  modelMetadata?: OpenAIModelMetadataResolver;
};

export class OpenAIProvider implements ModelProvider {
  #client?: OpenAI;
  #modelCache = new Map<string, Model>();
  #options: OpenAIProviderOptions;

  constructor(options: OpenAIProviderOptions = {}) {
    this.#options = options;
    if (this.#options.openAIClient) {
      if (this.#options.apiKey) {
        throw new Error('Cannot provide both apiKey and openAIClient');
      }
      if (this.#options.baseURL) {
        throw new Error('Cannot provide both baseURL and openAIClient');
      }
      this.#client = this.#options.openAIClient;
    }
  }

  getDefaultModelName(): string {
    return getDefaultModel();
  }

  getModelMetadata(modelName?: string): ModelMetadata | undefined {
    const resolvedModelName = modelName || getDefaultModel();
    return (
      resolveConfiguredModelMetadata(
        this.#options.modelMetadata,
        resolvedModelName,
      ) ?? getKnownOpenAIModelMetadata(resolvedModelName)
    );
  }

  #getClient(): OpenAI {
    if (!this.#client) {
      this.#client =
        getDefaultOpenAIClient() ??
        new OpenAI({
          apiKey: this.#options.apiKey ?? getDefaultOpenAIKey(),
          baseURL: this.#options.baseURL,
          organization: this.#options.organization,
          project: this.#options.project,
        });
    }
    return this.#client;
  }

  async getModel(modelName?: string): Promise<Model> {
    const model = modelName || getDefaultModel();
    const cacheKey = JSON.stringify([model, 'responses']);

    const cachedModel = this.#modelCache.get(cacheKey);
    if (cachedModel) {
      return cachedModel;
    }

    const resolvedModel = new OpenAIResponsesModel(
      this.#getClient(),
      model,
      this.getModelMetadata(model),
    );
    this.#modelCache.set(cacheKey, resolvedModel);
    return resolvedModel;
  }

  async close(): Promise<void> {
    const cachedModels = Array.from(new Set(this.#modelCache.values()));
    this.#modelCache.clear();

    const closeErrors: unknown[] = [];
    await Promise.all(
      cachedModels.map(async (model) => {
        const maybeClose = (model as { close?: () => Promise<void> | void })
          .close;
        if (typeof maybeClose !== 'function') {
          return;
        }
        try {
          await maybeClose.call(model);
        } catch (error) {
          closeErrors.push(error);
        }
      }),
    );

    if (closeErrors.length === 1) {
      throw closeErrors[0];
    }
    if (closeErrors.length > 1) {
      const error = new Error('Failed to close OpenAIProvider.');
      (error as Error & { causes?: unknown[] }).causes = closeErrors;
      throw error;
    }
  }
}

function resolveConfiguredModelMetadata(
  resolver: OpenAIModelMetadataResolver | undefined,
  modelName: string,
): ModelMetadata | undefined {
  if (!resolver) {
    return undefined;
  }
  if (typeof resolver === 'function') {
    return resolver(modelName);
  }
  return resolver[modelName] ?? resolver[modelName.toLowerCase()];
}

function getKnownOpenAIModelMetadata(
  modelName: string,
): ModelMetadata | undefined {
  const normalized = modelName.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }
  if (
    /^gpt-5\.5(?:-pro)?(?:-\d{4}-\d{2}-\d{2})?$/.test(normalized) ||
    /^gpt-5\.4(?:-pro)?(?:-\d{4}-\d{2}-\d{2})?$/.test(normalized)
  ) {
    return {
      contextWindowTokens: 1_050_000,
      maxOutputTokens: 128_000,
      multimodal: {
        imageInput: true,
        imageToolResult: 'synthetic-user',
      },
    };
  }
  if (/^gpt-5\.4-(?:mini|nano)(?:-\d{4}-\d{2}-\d{2})?$/.test(normalized)) {
    return {
      contextWindowTokens: 400_000,
      maxOutputTokens: 128_000,
      multimodal: {
        imageInput: true,
        imageToolResult: 'synthetic-user',
      },
    };
  }
  if (
    /^gpt-5(?:\.\d+)?(?:-(?:mini|nano|pro))?(?:-\d{4}-\d{2}-\d{2})?$/.test(
      normalized,
    ) ||
    /^gpt-4(?:\.1|o)(?:-(?:mini|nano|pro))?(?:-\d{4}-\d{2}-\d{2})?$/.test(
      normalized,
    )
  ) {
    return {
      multimodal: {
        imageInput: true,
        imageToolResult: 'synthetic-user',
      },
    };
  }
  return undefined;
}
